import { Badge } from '@/design-system/primitives';
import type {
  PatientSubscriptionContext,
  PatientSubscriptionPlan,
  PatientSubscriptionStatus,
} from '@/types/clinician';

type BadgeTone = 'success' | 'warning' | 'neutral' | 'danger';

const PLAN_LABELS: Record<PatientSubscriptionStatus, string> = {
  active: 'Active',
  canceling: 'Canceling',
  paused: 'Paused',
  ended: 'Canceled',
};

function planTone(status: PatientSubscriptionStatus): BadgeTone {
  if (status === 'active') return 'success';
  if (status === 'canceling' || status === 'paused') return 'warning';
  return 'neutral';
}

export function subscriptionDate(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function subscriptionSummary(subscription: PatientSubscriptionContext): {
  label: string;
  tone: BadgeTone;
  detail: string | null;
} {
  const canceled = subscriptionDate(subscription.last_cancelled_at);
  if (subscription.state === 'ended') {
    return {
      label: 'Inactive',
      tone: 'neutral',
      detail: canceled ? `Canceled ${canceled}` : 'No active subscriptions',
    };
  }
  if (subscription.state === 'canceling') {
    const ends = subscription.plans
      .filter((plan) => plan.status === 'canceling' && plan.ends_at)
      .map((plan) => String(plan.ends_at))
      .sort()[0];
    return {
      label: 'Cancellation scheduled',
      tone: 'warning',
      detail: subscriptionDate(ends)
        ? `Active until ${subscriptionDate(ends)}`
        : 'No future renewals',
    };
  }
  if (subscription.state === 'paused') {
    return {
      label: 'Paused',
      tone: 'warning',
      detail: 'Subscription currently paused',
    };
  }
  if (subscription.state === 'active') {
    const count = subscription.active_count;
    const canceling = subscription.canceling_count;
    return {
      label: count > 1 ? `${count} active plans` : 'Active plan',
      tone: 'success',
      detail:
        canceling > 0
          ? `${canceling} cancellation${canceling === 1 ? '' : 's'} scheduled`
          : canceled
            ? `Most recent cancellation ${canceled}`
            : null,
    };
  }
  return {
    label: 'No subscription',
    tone: 'neutral',
    detail: null,
  };
}

export function SubscriptionStatusSummary({
  subscription,
}: {
  subscription: PatientSubscriptionContext | undefined;
}) {
  if (!subscription) return null;
  const summary = subscriptionSummary(subscription);
  return (
    <div className="flex flex-wrap items-center gap-x-7 gap-y-4">
      <Badge tone={summary.tone} size="sm">
        {summary.label}
      </Badge>
      {summary.detail && (
        <span className="text-11 text-ink-muted">{summary.detail}</span>
      )}
    </div>
  );
}

function planDetail(plan: PatientSubscriptionPlan) {
  if (plan.status === 'ended') {
    const canceled = subscriptionDate(plan.cancelled_at);
    return canceled ? `Canceled ${canceled}` : 'Subscription ended';
  }
  if (plan.status === 'canceling') {
    const ends = subscriptionDate(plan.ends_at);
    return ends ? `Ends ${ends}` : 'Cancellation scheduled';
  }
  return null;
}

export function SubscriptionPlanList({
  subscription,
}: {
  subscription: PatientSubscriptionContext | undefined;
}) {
  if (!subscription?.plans.length) return null;
  return (
    <div className="border-t border-line-subtle pt-9 mt-3 flex flex-col gap-8">
      <span className="type-label-xs text-ink-muted">SUBSCRIPTION STATUS</span>
      {subscription.plans.map((plan) => {
        const detail = planDetail(plan);
        return (
          <div
            key={plan.subscription_id}
            className="flex flex-col gap-3 min-w-0"
          >
            <div className="flex items-start justify-between gap-6">
              <span className="type-body-sm font-medium break-words min-w-0">
                {plan.product}
              </span>
              <Badge tone={planTone(plan.status)} size="sm">
                {PLAN_LABELS[plan.status]}
              </Badge>
            </div>
            {detail && <span className="text-11 text-ink-muted">{detail}</span>}
          </div>
        );
      })}
    </div>
  );
}
